import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ArrowRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import AuthShowcase from "@/components/auth/AuthShowcase";

const SECTORS = [
  { value: "automotive", label: "Oficina mecânica" },
  { value: "automotive_dealer", label: "Concessionária" },
  // v0.96.24-26 — Starter Kit + Wave B multi-setor.
  { value: "clinic", label: "Clínica / Saúde" },
  { value: "salon", label: "Salão de Beleza / Barbearia" },
  { value: "petshop", label: "Pet Shop / Veterinária" },
  { value: "gym", label: "Academia / Studio / Personal" },
  { value: "condominium", label: "Condomínio / Administradora" },
  { value: "pharmacy", label: "Farmácia / Drogaria" },
  { value: "accounting_firm", label: "Contabilidade" },
  // Wave B
  { value: "law_firm", label: "Advocacia / Escritório de Advogados" },
  { value: "real_estate", label: "Imobiliária / Corretor de Imóveis" },
  { value: "insurance_broker", label: "Corretora de Seguros" },
  { value: "school", label: "Escola / Curso / Educação" },
  // Wave C
  { value: "restaurant", label: "Restaurante / Delivery" },
  { value: "hotel", label: "Hotel / Pousada" },
  { value: "events", label: "Eventos / Buffet / Casa de Festas" },
  { value: "supermarket", label: "Supermercado / Mercearia" },
  // Wave D
  { value: "construction_supply", label: "Materiais de Construção" },
  { value: "furniture_store", label: "Loja de Móveis / Decoração" },
  { value: "distributor", label: "Distribuidora / Atacado" },
  { value: "industry", label: "Indústria / Fábrica" },
  // Wave E
  { value: "logistics", label: "Transportadora / Logística" },
  { value: "tech_repair", label: "Assistência Técnica" },
  // Wave F — Prestadores de Serviços (com sub-especialidade dinâmica).
  { value: "service_provider", label: "Prestador de Serviços" },
  // v0.99.41 · Fev/2026 — Igreja (perfil pastoral com acolhimento,
  // células/ministérios, agendamentos de aconselhamento, aniversários).
  { value: "church", label: "Igreja / Comunidade de Fé" },
  // v0.99.46 · Fev/2026 — Mandato & Governo (gabinete único OU
  // estrutura institucional com múltiplos representantes).
  { value: "mandate", label: "Mandato & Governo" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    cnpj: "",
    sector: "automotive",
    // v0.96.30 (Wave F) — sub-especialidade obrigatória se sector=service_provider.
    service_specialty: "",
    name: "",
    phone: "",
    email: "",
    password: "",
    // v0.95.16 — dados fiscais (NF-e)
    fiscal_email: "",
    municipal_registration: "",
    state_registration: "",
  });
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  // ── CNPJ autofill (BrasilAPI) — v0.95.14 ──
  // fiscal.state: "idle" | "loading" | "ok" | "not_found" | "invalid" | "unreachable"
  const [fiscal, setFiscal] = useState({
    state: "idle",
    source: null,        // "brasilapi" | "manual" | null
    legal_name: "",
    trade_name: "",
    billing_address: null,
    lastLookupDigits: "",
  });
  // ── Aceite obrigatório (LGPD/Marco Civil). A confirmação da cláusula 7
  // (opt-in WhatsApp) está incluída no próprio aceite dos Termos para
  // manter a UI clean — a cláusula 7 declara explicitamente que ao
  // contratar o serviço o cliente declara possuir essa autorização.
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsVersion, setTermsVersion] = useState(null);
  // Iter v0.95 — Representative attribution.
  // The visitor may have arrived via `?rep=slug` (direct) OR via
  // `/r/:slug` (which RepLinkDispatcher redirects here with the param).
  // We persist the slug in localStorage so it survives subsequent
  // navigation (legal-terms page, password recovery, etc.) before
  // the user actually submits the form. The backend re-validates
  // the slug, so a stale/wrong cached value fails silently.
  const [repSlug, setRepSlug] = useState(null);
  const [repInfo, setRepInfo] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlSlug = (params.get("rep") || "").trim().toLowerCase();
    const stored = (
      localStorage.getItem("agentus_rep_slug") || ""
    ).trim().toLowerCase();
    const slug = urlSlug || stored;
    if (!slug) return;
    // Persist the URL slug for future visits in this session — but
    // do NOT overwrite an existing cached slug with an empty one
    // from a subsequent direct visit. First-rep-wins.
    if (urlSlug && urlSlug !== stored) {
      localStorage.setItem("agentus_rep_slug", urlSlug);
    }
    setRepSlug(slug);
    // Validate against the backend — if the slug is unknown/inactive,
    // we silently drop it so the signup proceeds without attribution.
    api.get(`/representatives/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => setRepInfo(r.data))
      .catch(() => {
        setRepSlug(null);
        localStorage.removeItem("agentus_rep_slug");
      });
  }, []);

  // Pull the currently-vigent terms version from the backend so the
  // signature is always against the freshest copy.
  useEffect(() => {
    let alive = true;
    api.get("/legal/terms/current")
      .then((r) => { if (alive) setTermsVersion(r.data.version); })
      .catch(() => {
        if (alive) {
          // Fall back to a known constant; backend will reject with a
          // human-readable error if it really is stale.
          setTermsVersion("1.0.0");
        }
      });
    return () => { alive = false; };
  }, []);

  // v0.96.30 (Wave F) — carrega sub-especialidades de "Prestador de Serviços"
  // sob demanda (só quando o usuário escolhe esse setor). Cachea em state.
  const [specialties, setSpecialties] = useState([]);
  const [specialtiesLoaded, setSpecialtiesLoaded] = useState(false);
  useEffect(() => {
    if (form.sector !== "service_provider" || specialtiesLoaded) return;
    let alive = true;
    api.get("/sector/service-provider/specialties")
      .then((r) => {
        if (!alive) return;
        setSpecialties(r.data?.specialties || []);
        setSpecialtiesLoaded(true);
      })
      .catch(() => { if (alive) setSpecialtiesLoaded(true); });
    return () => { alive = false; };
  }, [form.sector, specialtiesLoaded]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Brazilian phone mask: (XX) XXXXX-XXXX
  const setPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
    let masked = digits;
    if (digits.length > 0) masked = "(" + digits.slice(0, 2);
    if (digits.length >= 3) masked += ") " + digits.slice(2, 7);
    if (digits.length >= 8) masked += "-" + digits.slice(7, 11);
    setForm({ ...form, phone: masked });
  };

  // CNPJ mask: 00.000.000/0000-00
  const setCnpj = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 14);
    let m = digits;
    if (digits.length > 2)  m = digits.slice(0, 2) + "." + digits.slice(2);
    if (digits.length > 5)  m = digits.slice(0, 2) + "." + digits.slice(2, 5) + "." + digits.slice(5);
    if (digits.length > 8)  m = digits.slice(0, 2) + "." + digits.slice(2, 5) + "." + digits.slice(5, 8) + "/" + digits.slice(8);
    if (digits.length > 12) m = digits.slice(0, 2) + "." + digits.slice(2, 5) + "." + digits.slice(5, 8) + "/" + digits.slice(8, 12) + "-" + digits.slice(12);
    setForm({ ...form, cnpj: m });
    // Se o usuário editou o CNPJ, invalida o snapshot anterior.
    if (fiscal.state !== "idle" && fiscal.lastLookupDigits !== digits) {
      setFiscal({
        state: "idle", source: null, legal_name: "", trade_name: "",
        billing_address: null, lastLookupDigits: "",
      });
    }
  };

  // Local CNPJ checksum — mirror backend `is_valid_cnpj`. Evita chamar
  // BrasilAPI com CNPJ obviamente inválido.
  const isValidCnpj = (raw) => {
    const c = String(raw || "").replace(/\D/g, "");
    if (c.length !== 14) return false;
    if (c === c[0].repeat(14)) return false;
    const dv = (base, weights) => {
      let s = 0;
      for (let i = 0; i < weights.length; i++) s += Number(base[i]) * weights[i];
      const r = s % 11;
      return r < 2 ? 0 : 11 - r;
    };
    const w12 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const w13 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    if (dv(c.slice(0, 12), w12) !== Number(c[12])) return false;
    if (dv(c.slice(0, 13), w13) !== Number(c[13])) return false;
    return true;
  };

  const lookupCnpj = async () => {
    const digits = form.cnpj.replace(/\D/g, "");
    if (digits.length !== 14) return;
    if (!isValidCnpj(digits)) {
      setFiscal({
        state: "invalid", source: null, legal_name: "", trade_name: "",
        billing_address: null, lastLookupDigits: digits,
      });
      return;
    }
    // Evita re-lookup redundante para o MESMO CNPJ (idempotente).
    if (fiscal.state === "ok" && fiscal.lastLookupDigits === digits) return;
    setFiscal((f) => ({ ...f, state: "loading", lastLookupDigits: digits }));
    try {
      const r = await api.get(`/cnpj/lookup/${digits}`);
      const d = r?.data?.data || {};
      setFiscal({
        state: "ok",
        source: "brasilapi",
        legal_name: d.company_legal_name || "",
        trade_name: d.company_trade_name || "",
        billing_address: d.billing_address || null,
        lastLookupDigits: digits,
      });
      // Se o nome fantasia da BrasilAPI existe e o usuário ainda não
      // digitou o nome do estabelecimento, pré-preenchemos por cortesia.
      if (d.company_trade_name && !form.company_name.trim()) {
        setForm((s) => ({ ...s, company_name: d.company_trade_name }));
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        setFiscal({
          state: "not_found", source: null, legal_name: "", trade_name: "",
          billing_address: null, lastLookupDigits: digits,
        });
      } else if (code === 400) {
        setFiscal({
          state: "invalid", source: null, legal_name: "", trade_name: "",
          billing_address: null, lastLookupDigits: digits,
        });
      } else {
        // Timeout / 502 — permite cadastro manual sem bloquear.
        setFiscal({
          state: "unreachable", source: "manual", legal_name: "",
          trade_name: "", billing_address: null, lastLookupDigits: digits,
        });
      }
    }
  };
  const touch = (k) => () => setTouched((t) => ({ ...t, [k]: true }));

  // Inline validation — mirrors backend rules.
  const phoneDigits = form.phone.replace(/\D/g, "");
  const cnpjDigits = form.cnpj.replace(/\D/g, "");
  const issues = {
    company_name: form.company_name.trim().length === 0 ? null
      : form.company_name.trim().length < 2 ? "Informe o nome do estabelecimento." : null,
    cnpj: cnpjDigits.length === 0 ? null
      : cnpjDigits.length < 14 ? "CNPJ incompleto."
      : !isValidCnpj(cnpjDigits) ? "CNPJ inválido." : null,
    name: form.name.trim().length === 0 ? null
      : form.name.trim().length < 2 ? "Informe seu nome completo." : null,
    phone: phoneDigits.length === 0 ? null
      : phoneDigits.length < 10 ? "Telefone com DDD." : null,
    email: form.email.length === 0 ? null
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? "Email inválido." : null,
    fiscal_email: form.fiscal_email.length === 0 ? null
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.fiscal_email) ? "Email fiscal inválido." : null,
    password: form.password.length === 0 ? null
      : form.password.length < 8 ? "Mínimo de 8 caracteres." : null,
  };
  const firstIssueKey = Object.keys(issues).find((k) => {
    const v = form[k];
    return (typeof v === "string" ? v.trim() === "" : !v) || issues[k];
  });

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ company_name: true, cnpj: true, name: true, phone: true, email: true, password: true });
    if (!form.company_name.trim()) { toast.error("Informe o nome do estabelecimento."); return; }
    if (cnpjDigits.length !== 14 || !isValidCnpj(cnpjDigits)) {
      toast.error("Informe um CNPJ válido.");
      return;
    }
    if (!form.name.trim()) { toast.error("Informe seu nome completo."); return; }
    if (form.sector === "service_provider" && !form.service_specialty) {
      toast.error("Selecione a sua especialidade (eletricista, encanador, etc.).");
      return;
    }
    if (phoneDigits.length < 10) { toast.error("Informe o telefone com DDD."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error("Email inválido."); return; }
    if (!form.fiscal_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.fiscal_email)) {
      toast.error("Informe o email fiscal (usado para envio da NF-e).");
      return;
    }
    if (form.password.length < 8) { toast.error("Senha precisa ter pelo menos 8 caracteres."); return; }
    if (!acceptTerms) {
      toast.error("Você precisa aceitar os Termos de Uso e Política de Privacidade.");
      return;
    }
    if (!termsVersion) {
      toast.error("Aguardando os termos carregarem. Tente novamente.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        phone: phoneDigits,
        cnpj: cnpjDigits,                       // envia só dígitos — backend sanitiza também
        // v0.96.30 (Wave F) — só envia specialty se o setor exige
        service_specialty: form.sector === "service_provider" ? form.service_specialty : null,
        company_legal_name: fiscal.legal_name || null,
        company_trade_name: fiscal.trade_name || null,
        billing_address: fiscal.billing_address || null,
        fiscal_source: fiscal.source || "manual",
        fiscal_email: form.fiscal_email.trim().toLowerCase(),
        municipal_registration: form.municipal_registration.trim() || null,
        state_registration: form.state_registration.trim() || null,
        accept_terms: true,
        // A cláusula 7 dos Termos (autorização WhatsApp) é parte integrante
        // do mesmo aceite — não exibimos checkbox separado, mas registramos
        // a confirmação no backend pra manter a prova jurídica.
        confirm_whatsapp_optin: true,
        terms_version: termsVersion,
      };
      const intent = await register(payload);
      // intent = { registration_token, tenant_id, email }
      sessionStorage.setItem("agentus_checkout_intent", JSON.stringify(intent));
      nav("/checkout");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Falha ao criar conta";
      if (detail === "email already registered") {
        msg = "Este email já está cadastrado. Tente fazer login.";
      } else if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (err?.message === "Network Error") {
        msg = "Não foi possível conectar ao servidor. Verifique sua conexão.";
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white text-slate-900 flex overflow-hidden">
      {/* ───── LEFT — Dark showcase (desktop only) ───── */}
      <AuthShowcase />

      {/* ───── RIGHT — Auth surface ───── */}
      <div className="relative flex-1 lg:basis-1/2 lg:grow-0 lg:shrink-0 min-h-screen flex flex-col overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, #0F172A 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-50/50 to-transparent"
        />

        <header className="relative z-10 px-5 sm:px-7 pt-3 sm:pt-4 pb-1 flex items-center justify-between">
          <img src="/logo-black.png" alt="AgentUS" className="h-[14px] sm:h-[16px] w-auto select-none opacity-90 lg:hidden" draggable="false" data-testid="register-logo" />
          <span className="hidden lg:block" />
          <Link
            to="/login"
            className="text-[10.5px] tracking-tight text-slate-300 hover:text-slate-700 transition-colors duration-200"
            data-testid="register-to-login"
          >
            Já tenho conta →
          </Link>
        </header>

        <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-6 sm:py-8">
          <div className="w-full max-w-[420px]">
            <div className="text-center mb-6 sm:mb-7">
              <div className="text-[9.5px] uppercase tracking-[0.30em] text-slate-300 font-medium mb-3 select-none">
                Crie sua conta AgentUS
              </div>
              <h1 className="font-heading text-[22px] sm:text-[26px] font-semibold tracking-[-0.025em] text-slate-900 leading-[1.15]">
                Comece em 1 minuto
              </h1>
              <p className="text-[12.5px] text-slate-500 mt-1.5 tracking-tight">
                Cada conta é isolada. Clientes, regras e auditoria ficam só na sua operação.
              </p>
            </div>

          <form
            onSubmit={submit}
            data-testid="register-form"
            noValidate
            className="bg-white border border-slate-200/70 rounded-[14px] px-5 sm:px-6 pt-5 pb-5 shadow-[0_1px_0_rgba(15,23,42,0.03),0_8px_28px_-12px_rgba(15,23,42,0.07)] space-y-1.5"
          >
            <Field label="Empresa" error={touched.company_name ? issues.company_name : null}>
              <input
                value={form.company_name}
                onChange={set("company_name")}
                onBlur={touch("company_name")}
                placeholder="Oficina Alfa"
                data-testid="register-company-input"
                className={`reg-input ${touched.company_name && issues.company_name ? "reg-input--error" : ""}`}
              />
            </Field>

            <Field
              label="CNPJ"
              hint={
                fiscal.state === "loading" ? "Validando…"
                : fiscal.state === "ok" ? "Validado"
                : fiscal.state === "not_found" ? "Não encontrado — siga manual"
                : fiscal.state === "unreachable" ? "Consulta indisponível — siga manual"
                : "Obrigatório · Necessário para emissão de NF"
              }
              error={touched.cnpj ? issues.cnpj : null}
            >
              <input
                value={form.cnpj}
                onChange={setCnpj}
                onBlur={() => { touch("cnpj")(); lookupCnpj(); }}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                autoComplete="off"
                data-testid="register-cnpj-input"
                className={`reg-input ${touched.cnpj && issues.cnpj ? "reg-input--error" : ""}`}
              />
              {fiscal.state === "ok" && fiscal.legal_name ? (
                <div
                  data-testid="register-cnpj-fiscal-preview"
                  className="mt-1.5 rounded-[8px] border border-slate-200/70 bg-slate-50/60 px-2.5 py-1.5 text-[11.5px] leading-[1.35] text-slate-600"
                >
                  <div className="text-slate-900 font-medium truncate">
                    {fiscal.legal_name}
                  </div>
                  {fiscal.billing_address?.city ? (
                    <div className="text-slate-500 truncate">
                      {[
                        fiscal.billing_address.city,
                        fiscal.billing_address.state,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Field>

            <Field label="Setor">
              <div className="relative">
                <select
                  value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value, service_specialty: "" })}
                  data-testid="register-sector-select"
                  className="reg-input appearance-none pr-9 cursor-pointer"
                >
                  {SECTORS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <CaretDown size={12} weight="bold" className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              </div>
            </Field>

            {/* v0.96.30 (Wave F) — sub-especialidade dependente. */}
            {form.sector === "service_provider" && (
              <Field
                label="Sua especialidade"
                hint={
                  !specialtiesLoaded ? "Carregando…"
                    : specialties.length === 0 ? "Nenhuma especialidade disponível"
                    : "Obrigatório · define os fluxos e a IA"
                }
              >
                <div className="relative">
                  <select
                    value={form.service_specialty}
                    onChange={set("service_specialty")}
                    disabled={!specialtiesLoaded || specialties.length === 0}
                    data-testid="register-service-specialty-select"
                    className="reg-input appearance-none pr-9 cursor-pointer disabled:opacity-60 disabled:cursor-wait"
                  >
                    <option value="" disabled>
                      {specialtiesLoaded ? "Selecione…" : "Carregando…"}
                    </option>
                    {specialties.map((sp) => (
                      <option key={sp.code} value={sp.code}>{sp.display_name}</option>
                    ))}
                  </select>
                  <CaretDown size={12} weight="bold" className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                </div>
              </Field>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Seu nome" error={touched.name ? issues.name : null}>
                <input
                  value={form.name}
                  onChange={set("name")}
                  onBlur={touch("name")}
                  placeholder="Maria Silva"
                  data-testid="register-name-input"
                  className={`reg-input ${touched.name && issues.name ? "reg-input--error" : ""}`}
                />
              </Field>
              <Field label="Telefone" error={touched.phone ? issues.phone : null}>
                <input
                  value={form.phone}
                  onChange={setPhone}
                  onBlur={touch("phone")}
                  placeholder="(11) 99999-9999"
                  inputMode="numeric"
                  data-testid="register-phone-input"
                  className={`reg-input ${touched.phone && issues.phone ? "reg-input--error" : ""}`}
                />
              </Field>
            </div>

            <Field label="Email" error={touched.email ? issues.email : null}>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                onBlur={touch("email")}
                placeholder="seu@email.com"
                data-testid="register-email-input"
                className={`reg-input ${touched.email && issues.email ? "reg-input--error" : ""}`}
              />
            </Field>

            <Field
              label="Email fiscal (NF-e)"
              hint="Para onde enviaremos as notas fiscais mensais"
              error={touched.fiscal_email ? issues.fiscal_email : null}
            >
              <input
                type="email"
                value={form.fiscal_email}
                onChange={set("fiscal_email")}
                onBlur={touch("fiscal_email")}
                placeholder="contabilidade@empresa.com"
                data-testid="register-fiscal-email-input"
                className={`reg-input ${touched.fiscal_email && issues.fiscal_email ? "reg-input--error" : ""}`}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0">
              <Field label="Inscrição Municipal" hint="Opcional">
                <input
                  type="text"
                  value={form.municipal_registration}
                  onChange={set("municipal_registration")}
                  placeholder="Se sua cidade exigir"
                  data-testid="register-municipal-registration-input"
                  className="reg-input"
                />
              </Field>
              <Field label="Inscrição Estadual" hint="Opcional">
                <input
                  type="text"
                  value={form.state_registration}
                  onChange={set("state_registration")}
                  placeholder="Se aplicável"
                  data-testid="register-state-registration-input"
                  className="reg-input"
                />
              </Field>
            </div>

            <Field
              label="Senha"
              hint="Mínimo 8 caracteres"
              error={touched.password ? issues.password : null}
            >
              <input
                type="password"
                value={form.password}
                onChange={set("password")}
                onBlur={touch("password")}
                placeholder="••••••••"
                data-testid="register-password-input"
                className={`reg-input ${touched.password && issues.password ? "reg-input--error" : ""}`}
              />
            </Field>

            {/* ── Aceite obrigatório dos Termos (inclui cláusula 7 — opt-in WhatsApp) ── */}
            <div className="pt-1">
              <label
                className="flex items-start gap-2.5 cursor-pointer group"
                data-testid="register-terms-label"
              >
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  data-testid="register-accept-terms"
                  className="mt-[3px] h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-1 focus:ring-slate-900 focus:ring-offset-0 shrink-0 cursor-pointer"
                />
                <span className="text-[12px] leading-[1.5] text-slate-600 group-hover:text-slate-900 transition-colors">
                  Li e aceito os{" "}
                  <Link
                    to="/legal/termos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-900 font-medium underline underline-offset-[3px] decoration-slate-400 hover:decoration-slate-900"
                    data-testid="register-terms-link"
                  >
                    Termos de Uso e Política de Privacidade
                  </Link>
                  .
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="reg-submit"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full border-[1.5px] border-white/25 border-t-white animate-spin" />
                  Criando
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 group/btn">
                  Continuar para o pagamento
                  <ArrowRight size={13} weight="bold" className="opacity-60 transition-all duration-300 ease-out group-hover/btn:opacity-100 group-hover/btn:translate-x-0.5" />
                </span>
              )}
            </button>
          </form>

          <p className="text-[12px] text-slate-500 text-center mt-4 tracking-tight">
            Já tem conta?{" "}
            <Link to="/login" className="text-slate-900 font-semibold hover:underline underline-offset-[5px] decoration-[1.5px] transition-all">
              Entrar
            </Link>
          </p>
        </div>
      </main>

      <footer className="relative z-10 px-6 py-3 text-center">
        <p className="text-[10px] text-slate-300 tracking-[0.04em]">
          © {new Date().getFullYear()} AgentUS · multi-tenant · audit-first · API-ready
        </p>
      </footer>
      </div>{/* /RIGHT auth surface */}

      <Toaster position="top-right" richColors />

      <style>{`
        .reg-input {
          width: 100%;
          height: 42px;
          background: #FAFBFC;
          border: 1px solid #E5E9EF;
          border-radius: 10px;
          padding: 0 13px;
          font-size: 13.5px;
          color: #0F172A;
          outline: none;
          transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
        }
        .reg-input::placeholder { color: #94A3B8; }
        .reg-input:hover { border-color: #CBD5E1; }
        .reg-input:focus {
          background: #FFFFFF;
          border-color: #0F172A;
          box-shadow: 0 0 0 3px rgba(15,23,42,0.05);
        }
        .reg-input--error,
        .reg-input--error:focus {
          border-color: #F43F5E;
          box-shadow: 0 0 0 3px rgba(244,63,94,0.10);
        }
        @media (min-width: 640px) {
          .reg-input { height: 44px; font-size: 14px; }
        }

        .reg-submit {
          margin-top: 8px;
          width: 100%;
          height: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #0F172A;
          color: #FFFFFF;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.005em;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.06),
            0 1px 2px rgba(15,23,42,0.18);
          transition:
            background-color .25s ease,
            box-shadow .25s ease,
            transform .15s ease;
        }
        .reg-submit:hover {
          background: #1E293B;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,0.08),
            0 4px 14px -3px rgba(15,23,42,0.35);
        }
        .reg-submit:active {
          background: #020617;
          transform: translateY(0.5px);
        }
        .reg-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-medium">{label}</span>
        {hint && !error && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
      <div className="min-h-[16px] mt-1 text-[11.5px]">
        {error ? (
          <span className="inline-flex items-center gap-1 text-rose-600">
            <span className="w-1 h-1 rounded-full bg-rose-500" />
            {error}
          </span>
        ) : null}
      </div>
    </label>
  );
}
